/**
 * Mascot Domain — Djay Mascot System
 *
 * Barrel export for the Djay mascot components, hooks, animations, and messages.
 * DDD co-locatie: moved from components/mascot/ in s60.
 *
 * Usage:
 * ```tsx
 * import { DjayAvatar, DjayBubble, getDjayGreeting, djayVariants } from '../mascot'
 * ```
 */

// Core components
export { DjayAvatar, VARIANT_COLORS } from './DjayAvatar'
export type {
  DjaySize,
  DjayEmotion,
  DjayTier,
  DjayVariant,
  DjayAccessory,
  DjayAvatarProps,
} from './DjayAvatar'

export { DjayBubble } from './DjayBubble'
export type { DjayBubbleProps, BubbleColorScheme } from './DjayBubble'

export { FloatingDjay } from './FloatingDjay'

// NOTE: DjayShowcase is intentionally NOT re-exported from this barrel.
// It is a 600+ line admin-only configurator and must be loaded via
// `React.lazy(() => import('../../../mascot/DjayShowcase'))` from
// `portals/admin/pages/MascotPage.tsx`. Exporting it through the barrel
// would defeat the code-split and re-bloat the learner bundle.

export { DjayMeesterLogo } from './DjayMeesterLogo'
export type { DjayMeesterLogoProps } from './DjayMeesterLogo'

export { BrandLockup } from './BrandLockup'
export type { BrandLockupProps } from './BrandLockup'

// DjayToetsAvatar: assessment-mode mascot (added to barrel during DDD audit
// Phase A4 to remove deep-import bypasses from assessment/).
export { DjayToetsAvatar } from './DjayToetsAvatar'

// Hooks
export { useDjaySelection } from './useDjaySelection'
export { useVariantUnlocks, TOTAL_VARIANTS } from './useVariantUnlocks'
export type {
  VariantUnlockInfo,
  VariantProgress,
  MascotRarity,
} from './useVariantUnlocks'
export { useDjayCustomization } from './useDjayCustomization'
export type {
  AccessoryUnlockInfo,
  TierUnlockInfo,
} from './useDjayCustomization'
export { useLearnerContext } from './useLearnerContext'

// Config (timings + thresholds — single source of truth for magic numbers)
export { DJAY_TIMINGS, DJAY_THRESHOLDS, DJAY_TIME_EGGS } from './config'

// Messages / Copy Registry
export {
  getDjayGreeting,
  getDjayPersonalGreeting,
  getDjayDailyTip,
  getDjayThinkingMessage,
  getDjayMissionHeading,
  getDjayLabWelcome,
  getDjayModeIntro,
  getDjayViewMessage,
  getDjayIdleMessage,
  getDjaySplashMessage,
  getDjayDistractorIntro,
  getDjayExplanationIntro,
  getDjayWelcomeMessage,
  getDjayRegistrationStep,
} from './djayMessages'
export type { DjayMessage, LearnerContext } from './djayMessages'

// Easter Eggs (DDD co-locatie: mascot domain)
export { useEasterEggs, useLongPressDiscovered } from './useEasterEggs'
export {
  EASTER_EGGS,
  RAPID_TAP_ESCALATION,
  checkTimeBasedEgg,
  createRapidTapDetector,
  createKonamiDetector,
  getRapidTapReaction,
} from './easterEggs'
export type {
  EasterEggId,
  EasterEggReaction,
  EasterEggDefinition,
  EasterEggSound,
} from './easterEggs'

// Animations
export { djayVariants } from './animations/transitions'

// Illustrations
export { getAchievementIllustration } from './illustrations'
export { DjayIllustrationBase } from './illustrations'
export type { DjayIllustrationProps, AchievementRarity } from './illustrations'
